import * as Sentry from '@sentry/react';

type StorageKind = 'local' | 'session';

const PROBE_KEY = '__storage_probe__';
// Probe payload sized to represent a realistic JWT write (~860 bytes + overhead).
// A 1-byte probe always succeeds even under quota pressure, giving a false
// `storage.local: ok` signal.
const PROBE_VALUE = 'x'.repeat(2048);

export interface StorageAvailabilityReport {
  local: boolean;
  session: boolean;
}

function probe(kind: StorageKind): boolean {
  try {
    const storage = kind === 'local' ? window.localStorage : window.sessionStorage;
    storage.setItem(PROBE_KEY, PROBE_VALUE);
    const readback = storage.getItem(PROBE_KEY);
    storage.removeItem(PROBE_KEY);
    return readback === PROBE_VALUE;
  } catch {
    return false;
  }
}

let reported = false;

export function reportStorageAvailability(): StorageAvailabilityReport {
  const report: StorageAvailabilityReport = {
    local: probe('local'),
    session: probe('session'),
  };

  if (reported) return report;
  reported = true;

  // Tags ride on every subsequent event so downstream failures
  // (phantom-auth, JWT write mismatches) carry the storage status without a
  // standalone issue. Safari private mode / ITP blocks storage on millions of
  // sessions with no action we can take — capturing it separately was pure
  // noise in Sentry.
  Sentry.setTag('storage.local', report.local ? 'ok' : 'blocked');
  Sentry.setTag('storage.session', report.session ? 'ok' : 'blocked');

  return report;
}
